import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js'
import postalCodes from 'postal-codes-js'

import {
  VALIDATOR_CONTAINS_INVALID_USERNAME_SYMBOLS,
  VALIDATOR_EXPIRED_EXPIRATION_DATE,
  VALIDATOR_INVALID_CREDIT_CARD_NUMBER,
  VALIDATOR_INVALID_EXPIRATION_MONTH,
  VALIDATOR_INVALID_CREDIT_CARD,
  VALIDATOR_INVALID_MEMBER_NUMBER,
  VALIDATOR_INVALID_PHONE_NUMBER,
  VALIDATOR_INVALID_PHONE_NUMBER_FOR_X,
  VALIDATOR_INVALID_POSTAL_CODE_FOR_X,
  VALIDATOR_INVALID_USERNAME,
  VALIDATOR_MATCHES_FORMAT_X,
  VALIDATOR_MAX_LENGTH_X,
  VALIDATOR_MIN_LENGTH_X,
  VALIDATOR_PASSWORD_STRENGTH_NOT_MET,
  VALIDATOR_REQUIRES_X_LOWERCASE,
  VALIDATOR_REQUIRES_X_NUMBERS,
  VALIDATOR_REQUIRES_X_SYMBOLS,
  VALIDATOR_REQUIRES_X_UPPERCASE,
  VALIDATOR_STARTS_WITH_LETTER,
  VALIDATOR_VALUES_DO_NOT_MATCH,
} from '@constants'
import { IFormField } from '@hooks/useForm'
import { convert3DigitCountryCodeTo2Digit } from '@utils/convert3DigitCountryCodeTo2Digit'
import validator from 'validator'

export type TValidatorFn = (value: any, values?: Record<string, any>) => string | null | undefined

export const customFormValidators = {
  /** Validates that the value meets a maximum string length
   *
   * @param value
   * @param max
   * @param message
   */
  maxLength: (value: string, max: number, message?: string): string | null => {
    if (!value) return null
    if (value.length > max) {
      return message || VALIDATOR_MAX_LENGTH_X(max)
    }
    return null
  },

  /** Validates that the value meets a minimum string length
   *
   * @param value
   * @param min
   * @param message
   */
  minLength: (value: string, min: number, message?: string): string | null => {
    if (!value) return null
    if (value.length < min) {
      return message || VALIDATOR_MIN_LENGTH_X(min)
    }
    return null
  },

  /** Validates that the value matches another field's value
   *
   * @param otherFieldId
   * @param message
   */
  requiredMatch:
    (otherFieldId: string, message = VALIDATOR_VALUES_DO_NOT_MATCH): TValidatorFn =>
    (value, values) => {
      if (value !== values?.[otherFieldId]) return message
      return null
    },

  /** Validates that a password meets strength requirements
   *
   * @param value
   */
  passwordStrength: (value: string): string | null => {
    if (!value) return null
    const minLength = !customFormValidators.minLength(value, 8)
    const hasNumber = !customFormValidators.containsNumbers(value, 1)
    const hasSymbol = !customFormValidators.containsSymbols(value, 1) // todo: may not symbol
    const hasLower = !customFormValidators.containsLowercase(value, 1)
    const hasUpper = !customFormValidators.containsUppercase(value, 1)
    // todo: may not need symbol
    if (!minLength || !hasLower || !hasUpper || !hasNumber || !hasSymbol) {
      return VALIDATOR_PASSWORD_STRENGTH_NOT_MET
    }
    return null
  },

  /** Validates that a username meets specific validity requirements
   *
   * @param message
   */
  isValidUsername:
    (message?: string): TValidatorFn =>
    (value: string): string | null => {
      if (!value) return null
      const startsWithLetterValid = !customFormValidators.startsWithLetter(value)
      const minLengthValid = !customFormValidators.minLength(value, 6)
      const allowedChars = /^[A-Za-z0-9.',&-_]+$/.test(value)
      if (!startsWithLetterValid || !minLengthValid || !allowedChars) {
        return message || VALIDATOR_INVALID_USERNAME
      }
      return null
    },

  /** Checks for presence of at least 1 lowercase characters but allows the min to be specified
   *
   * @param value
   * @param min
   */
  containsLowercase: (value: string, min: number = 1): string | null => {
    if (!value) return null
    const matches = value.match(/[a-z]/g)
    const count = matches ? matches.length : 0
    if (count < min) {
      return VALIDATOR_REQUIRES_X_LOWERCASE(min)
    }
    return null
  },

  /** Checks for presence of at least 1 numeric characters but allows the min to be specified
   *
   * @param value
   * @param min
   */
  containsNumbers: (value: string, min: number = 1): string | null => {
    if (!value) return null
    const matches = value.match(/\d/g)
    const count = matches ? matches.length : 0
    if (count < min) {
      return VALIDATOR_REQUIRES_X_NUMBERS(min)
    }
    return null
  },

  /** Checks for presence of at least 1 uppercase characters but allows the min to be specified
   *
   * @param value
   * @param min
   */
  containsUppercase: (value: string, min: number = 1): string | null => {
    if (!value) return null
    const matches = value.match(/[A-Z]/g)
    const count = matches ? matches.length : 0
    if (count < min) {
      return VALIDATOR_REQUIRES_X_UPPERCASE(min)
    }
    return null
  },

  /** Checks for presence of at least 1 non-alphanumeric characters but allows the min to be specified
   *
   * @param value
   * @param min
   */
  containsSymbols: (value: string, min: number = 1): string | null => {
    if (!value) return null
    const matches = value.match(/[^A-Za-z0-9]/g)
    const count = matches ? matches.length : 0
    if (count < min) {
      return VALIDATOR_REQUIRES_X_SYMBOLS(min)
    }
    return null
  },

  /** Checks if the value contains symbols other than letters, numbers, or .',&-_
   *
   * @param value
   */
  containsInvalidUsernameCharacters: (value: string): string | null => {
    if (!value) return null
    const matches = value.match(/[^A-Za-z0-9.',&_-]/g)
    if (matches && matches.length > 0) {
      return VALIDATOR_CONTAINS_INVALID_USERNAME_SYMBOLS
    }
    return null
  },

  /** Accepts a 2-3-digit country code and uses validator to determine if the value is a valid postal code for that country. If the code is 3 digits, it will be converted to 2 digits using i18n-iso-countries.
   *
   * @param countryCode Defaults to USA
   * @param message Optional custom error message
   */
  isPostalCode:
    (countryCode: string = 'USA', message?: string): TValidatorFn =>
    (value) => {
      if (!value) return null
      // PostalCodes.validate returns true/false or a string error message
      const isValid = postalCodes.validate(countryCode, value)
      return typeof isValid !== 'string' ? null : message || VALIDATOR_INVALID_POSTAL_CODE_FOR_X(countryCode)
    },

  /** Validates that the value is a valid international phone number.
   * NOTE: This validator expects value to be of type IFormField with phoneNumber and countryCode properties
   *
   * @param message Optional custom error message
   */
  isInternationalPhone:
    (message?: string): TValidatorFn =>
    (value) => {
      if (!value || typeof value !== 'object') return null
      const { phoneNumber, countryCode } = value as IFormField
      const countryCode2Digit = convert3DigitCountryCodeTo2Digit(countryCode)
      if (!phoneNumber || !countryCode || !countryCode2Digit) return null
      const normalized = phoneNumber.replace(/[^\d]/g, '')
      try {
        if (!isValidPhoneNumber(normalized, countryCode2Digit as CountryCode)) {
          return message || VALIDATOR_INVALID_PHONE_NUMBER_FOR_X(countryCode)
        }
        return null
      } catch {
        return message || VALIDATOR_INVALID_PHONE_NUMBER
      }
    },

  /** Checks for a valid member number: numeric only, 9-11 characters
   *
   * @param message
   */
  isMemberNumber:
    (message?: string): TValidatorFn =>
    (value: string): string | null => {
      if (!value) return null
      const correctLength = value.length === 11
      const numeric = validator.isNumeric(value)
      if (!correctLength || !numeric) {
        return message || VALIDATOR_INVALID_MEMBER_NUMBER
      }
      return null
    },

  /** Validates that the value starts with a letter (a-z, A-Z)
   *
   * @param value
   */
  startsWithLetter: (value: string): string | null => {
    if (!value) return null
    if (!/^[A-Za-z]/.test(value)) {
      return VALIDATOR_STARTS_WITH_LETTER
    }
    return null
  },

  /** Validates credit card number using Luhn algorithm
   * Checks length (13-19 digits) and validates checksum
   *
   * @param value
   * @param message Optional custom error message
   */
  isCreditCardNumber: (value: string, message?: string): string | null => {
    if (!value) return null

    // Remove all non-digit characters
    const cardNumber = value.replace(/\D/g, '')

    // Check length (13-19 digits for valid credit cards)
    if (cardNumber.length < 13 || cardNumber.length > 19) {
      return message || VALIDATOR_INVALID_CREDIT_CARD
    }

    // Luhn algorithm implementation
    let sum = 0
    let isEven = false

    // Loop through values starting from the rightmost digit
    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber.charAt(i), 10)

      if (isEven) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      isEven = !isEven
    }

    // Valid if sum is divisible by 10
    if (sum % 10 !== 0) {
      return message || VALIDATOR_INVALID_CREDIT_CARD
    }

    return null
  },

  /** Validates contract number: alphanumeric, 1-12 characters
   *
   * @param message Optional custom error message
   */
  isContractNumber:
    (message?: string): TValidatorFn =>
    (value: string): string | null => {
      if (!value) return null
      const isValidLength = validator.isLength(value, { min: 1, max: 12 })
      const isAlphanumeric = validator.isAlphanumeric(value, 'en-US')
      if (!isValidLength || !isAlphanumeric) {
        return message || 'Contract number must be alphanumeric and 1-12 characters long.'
      }
      return null
    },

  /** Validates a credit card number:
   *  - Strips formatting (spaces, hyphens)
   *  - Detects card type by prefix
   *  - Validates length according to card type
   *  - Performs Luhn check
   *
   * @param message Optional custom error message
   */
  isValidCreditCardNumber:
    (message?: string): TValidatorFn =>
    (value) => {
      if (!value) return null

      // Remove spaces + hyphens
      const sanitized = value.replace(/\D/g, '')

      // All digits?
      if (!/^\d+$/.test(sanitized)) {
        return message || VALIDATOR_INVALID_CREDIT_CARD_NUMBER
      }

      // Detect card type by prefix
      let cardType: string | null = null
      if (/^4\d{12}(\d{3})?(\d{3})?$/.test(sanitized)) {
        cardType = 'Visa'
      } else if (/^5[1-5]\d{14}$/.test(sanitized) || /^2(?:2[2-9]\d|[3-7]\d{2})\d{12}$/.test(sanitized)) {
        cardType = 'Mastercard'
      } else if (/^3[47]\d{13}$/.test(sanitized)) {
        cardType = 'AMEX'
      } else if (/^6(?:011|5\d{2})\d{12}$/.test(sanitized)) {
        cardType = 'Discover'
      } else {
        return message || VALIDATOR_INVALID_CREDIT_CARD_NUMBER
      }

      // Expected lengths per type
      const cardTypeLengths: Record<string, number[]> = {
        Visa: [13, 16, 19],
        Mastercard: [16],
        AMEX: [15],
        Discover: [16, 19],
      }

      const validLengths = cardTypeLengths[cardType]
      if (!validLengths.includes(sanitized.length)) {
        return message || VALIDATOR_INVALID_CREDIT_CARD_NUMBER
      }

      if (!validator.isLuhnNumber(sanitized)) {
        return message || VALIDATOR_INVALID_CREDIT_CARD_NUMBER
      }

      return null
    },

  /** Validates that an expiration date is in MM/YY format, has a valid month,
   *  and represents the current month or a future month.
   *
   * @param message Optional custom error message
   */
  isValidExpirationDate:
    (message?: string): TValidatorFn =>
    (value) => {
      if (!value) return null

      // Must match MM/YY formatting
      const formatRegex = /^\d{2}\/\d{2}$/
      if (!formatRegex.test(value)) {
        return message || VALIDATOR_MATCHES_FORMAT_X('MM/YY')
      }

      const [mmStr, yyStr] = value.split('/')
      const month = Number(mmStr)
      const year = Number(yyStr)

      if (month < 1 || month > 12) {
        return message || VALIDATOR_INVALID_EXPIRATION_MONTH
      }

      // Convert YY -> YYYY
      const fullYear = 2000 + year

      const now = new Date()
      const currentMonth = now.getMonth() + 1 // 1–12
      const currentYear = now.getFullYear()

      // Compare year first
      if (fullYear < currentYear) {
        return message || VALIDATOR_EXPIRED_EXPIRATION_DATE
      }

      // If same year, check month
      if (fullYear === currentYear && month < currentMonth) {
        return message || VALIDATOR_EXPIRED_EXPIRATION_DATE
      }

      return null
    },

  /** Validates that a value matches a specified regex pattern.
   *
   * @param regex The pattern the value must match
   * @param formatLabel Label describing the required format (e.g. "XX/XX")
   * @param message Optional custom error message
   */
  matchesFormat:
    (regex: RegExp, formatLabel: string, message: string = VALIDATOR_MATCHES_FORMAT_X(formatLabel)): TValidatorFn =>
    (value) => {
      if (!value || !formatLabel) return null
      return regex.test(value) ? null : message
    },
}
